import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "../lib/api";
import { ArrowUp, ArrowDown, ArrowUpDown, Search } from "lucide-react";

// Generic placeholder for any non-BDC database type (reit/preferred/etf/dividend_growth/other).
// Uses the same /api/database/by-type/{type} endpoint and a simpler table.
// When you build a type-specific page later (e.g. DatabaseREIT.jsx), it
// replaces this placeholder via the App.js route table.

const TYPE_META = {
  reit:             { label: "REITs",            blurb: "Real Estate Investment Trusts. Yield + AFFO multiples coming in a later session." },
  preferred:        { label: "Preferred Stock",  blurb: "Preferred shares. Coupon / call structure column set coming." },
  etf:              { label: "ETFs",             blurb: "Exchange-traded funds. Expense ratio + holdings rollup coming." },
  dividend_growth:  { label: "Dividend Growth",  blurb: "Common stocks with 5+ consecutive years of dividend increases." },
  other:            { label: "Other",            blurb: "Anything not yet classified or that doesn't fit a primary asset class." },
};

export default function DatabaseGeneric() {
  const { type } = useParams();
  const meta = TYPE_META[type] || { label: type, blurb: "" };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState({ key: "market_cap_usd", dir: "desc" });

  useEffect(() => {
    setLoading(true);
    api.get(`/database/by-type/${type}`).then(({ data }) => setRows(data || []))
       .catch(() => setRows([]))
       .finally(() => setLoading(false));
  }, [type]);

  const COLS = useMemo(() => ([
    { key: "ticker", label: "Ticker", align: "left", acc: (r) => (r.ticker || "").toUpperCase() },
    { key: "name", label: "Name", align: "left", acc: (r) => (r.name || "").toLowerCase() },
    { key: "current_price", label: "Price", align: "right", acc: (r) => r.current_price || 0 },
    { key: "market_cap_usd", label: "Mkt Cap", align: "right", acc: (r) => r.market_cap_usd || 0 },
    { key: "annual_dividend", label: "Div FWD", align: "right", acc: (r) => r.annual_dividend || 0 },
    { key: "dividend_yield", label: "Yield", align: "right", acc: (r) => r.dividend_yield || 0 },
    ...(type === "dividend_growth"
        ? [{ key: "consecutive_dividend_increases", label: "Streak (yrs)", align: "right", acc: (r) => r.consecutive_dividend_increases || 0 }]
        : []),
    { key: "industry", label: "Industry", align: "left", acc: (r) => (r.industry || "").toLowerCase() },
  ]), [type]);

  const display = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let out = q
      ? rows.filter((r) => (r.ticker || "").toLowerCase().includes(q) || (r.name || "").toLowerCase().includes(q))
      : rows;
    if (sort.key) {
      const col = COLS.find((c) => c.key === sort.key);
      if (col) {
        const dir = sort.dir === "desc" ? -1 : 1;
        out = [...out].sort((a, b) => {
          const va = col.acc(a), vb = col.acc(b);
          if (va < vb) return -1 * dir;
          if (va > vb) return 1 * dir;
          return 0;
        });
      }
    }
    return out;
  }, [rows, filter, sort, COLS]);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "desc" };
      if (prev.dir === "desc") return { key, dir: "asc" };
      return { key: null, dir: null };
    });
  };

  return (
    <div className="min-h-screen bg-champagne">
      <div className="border-b border-stone px-8 py-5 sticky top-0 bg-champagne z-10 flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold">// SYS::DATABASE::{(type || "").toUpperCase()}</div>
          <h1 className="text-2xl font-bold tracking-tight text-graphite" data-testid={`db-${type}-title`}>{meta.label}</h1>
          <p className="text-xs text-graphite/55 mt-1 max-w-2xl">{rows.length} tickers · {meta.blurb}</p>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-graphite/45" />
          <input
            data-testid={`db-${type}-filter`}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter…"
            className="pl-9 pr-3 py-2 border border-stone bg-champagne text-sm w-64 focus:outline-none focus:border-paars-dark"
          />
        </div>
      </div>
      <div className="p-8">
        <div className="border border-stone bg-champagne overflow-x-auto" data-testid={`db-${type}-table`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone/95 border-b border-stone">
                {COLS.map((col) => {
                  const isSorted = sort.key === col.key;
                  const Icon = isSorted ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
                  return (
                    <th
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      className={`px-3 py-3 text-[10px] tracking-[0.2em] uppercase font-bold cursor-pointer hover:text-paars-dark ${
                        col.align === "right" ? "text-right" : "text-left"
                      } ${isSorted ? "text-paars-dark" : "text-graphite/60"}`}
                    >
                      <span className={`inline-flex items-center gap-1 ${col.align === "right" ? "justify-end" : ""}`}>
                        {col.label}
                        <Icon className={`w-3 h-3 ${isSorted ? "opacity-100" : "opacity-30"}`} strokeWidth={2.5} />
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={COLS.length} className="p-6 text-center font-mono text-xs text-graphite/45">LOADING…</td></tr>}
              {!loading && display.length === 0 && (
                <tr><td colSpan={COLS.length} className="p-6 text-center font-mono text-xs text-graphite/55">
                  No {meta.label} in the database yet. {type === "dividend_growth" && "Tag instruments with 5+ years of dividend growth via the admin instrument editor."}
                </td></tr>
              )}
              {display.map((r) => (
                <tr key={r.ticker} data-testid={`db-${type}-row-${r.ticker}`} className="border-b border-stone/60 hover:bg-white">
                  <td className="px-3 py-3 font-mono font-semibold text-paars-dark">{r.ticker}</td>
                  <td className="px-3 py-3 text-graphite/70 max-w-[280px] truncate">{r.name || "—"}</td>
                  <td className="px-3 py-3 font-mono text-right">{r.current_price ? fmtMoney(r.current_price) : "—"}</td>
                  <td className="px-3 py-3 font-mono text-right">{r.market_cap_usd ? `$${(r.market_cap_usd / 1_000_000_000).toFixed(2)}B` : "—"}</td>
                  <td className="px-3 py-3 font-mono text-right">{r.annual_dividend != null ? fmtMoney(r.annual_dividend) : "—"}</td>
                  <td className="px-3 py-3 font-mono text-right text-paars-dark">{r.dividend_yield != null ? fmtPct(r.dividend_yield) : "—"}</td>
                  {type === "dividend_growth" && (
                    <td className="px-3 py-3 font-mono text-right text-groen-dark">{r.consecutive_dividend_increases ?? "—"}</td>
                  )}
                  <td className="px-3 py-3 text-graphite/65 max-w-[200px] truncate">{r.industry || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
